// Runs a report template with the user's own LLM key and keeps the result in this browser.
import { buildHoldingsContext } from '@/experts/context';
import { llmConfig } from '@/experts/run';
import { currentLocale, t } from '@/i18n';
import { describeError } from '@/llm/types';
import type { ChatMeta } from '@/llm/types';
import { fetchBenchmarks, formatBenchmarks } from '@/market/benchmarks';
import { todayNY } from '@/market/marketHours';
import { refreshAll } from '@/market/quoteService';
import { computeTotals } from '@/market/value';
import { settingsStore } from '@/settings/settingsStore';
import { appStore } from '@/state/appStore';
import { heldOnly, type Report, type ReportTemplateId } from '@/types';
import { actualCost } from './cost';
import { assemblePrompt } from './prompt';
import { heldBy, MAX_SEARCHES, templateById, templateName } from './templates';

export function todaysReport(templateId: ReportTemplateId, date = todayNY()): Report | undefined {
  return Object.values(appStore.get().reports).find((r) => r && r.templateId === templateId && r.date === date);
}

export function latestReport(templateId?: ReportTemplateId): Report | undefined {
  return Object.values(appStore.get().reports)
    .filter((r): r is Report => !!r && (!templateId || r.templateId === templateId))
    .sort((a, b) => b.generatedAt - a.generatedAt)[0];
}

export function reportsByRecency(): Report[] {
  return Object.values(appStore.get().reports).filter((r): r is Report => !!r).sort((a, b) => b.generatedAt - a.generatedAt);
}

/** Generates one report; daily templates replace the same day's earlier run. Notifies on failure and rethrows. */
export async function generateReport(templateId: ReportTemplateId, opts: { scenario?: string } = {}): Promise<Report> {
  const template = templateById(templateId);
  const cfg = llmConfig();
  const assets = heldOnly(appStore.get().assets); // watch items are not reported on
  if (assets.length === 0) throw new Error(t('report.noAssets'));

  appStore.setReportBusy(templateId);
  try {
    // Outside the session quotes are only fetched once per phase; a report states prices, so it starts from fresh ones
    // (a closing report needs the final close). Never throws: on failure the cached quotes are used.
    // Benchmarks ride along so the market section never depends on a search finding the closes.
    const [, benchmarks] = await Promise.all([refreshAll(true), fetchBenchmarks({ canada: heldBy(assets).canada })]);
    const { quotes, fx } = appStore.get();
    const locale = currentLocale();
    const now = Date.now();
    const bench = formatBenchmarks(benchmarks);
    const context = bench ? `${buildHoldingsContext(assets, quotes, fx)}\n\n${bench}` : buildHoldingsContext(assets, quotes, fx);
    const totals = computeTotals(assets, quotes, fx);
    const maxSearches = Math.min(MAX_SEARCHES, template.estimateSearches(assets) + 4);
    const prompt = assemblePrompt(template, { context, assets, now, locale, maxSearches, scenario: opts.scenario });

    let meta: ChatMeta = {};
    let markdown = await cfg.adapter.chat({
      system: prompt.system,
      messages: [{ role: 'user', content: [{ type: 'text', text: prompt.user }] }],
      model: cfg.model,
      apiKey: cfg.apiKey,
      webSearch: template.needsWeb ? { maxUses: maxSearches } : undefined,
      onMeta: (m) => {
        meta = m;
      },
    });
    markdown = markdown.trim();
    // Providers that cite out-of-band (Gemini grounding, OpenAI annotations) leave the text without links; list them.
    if (meta.sources?.length && !/\]\(https?:\/\//.test(markdown)) {
      markdown += `\n\n## ${t('report.sourcesHeading')}\n${meta.sources.map((s) => `- [${s.title || s.url}](${s.url})`).join('\n')}`;
    }

    const provider = settingsStore.get().llm.activeProvider;
    const usage = { inputTokens: meta.inputTokens, outputTokens: meta.outputTokens, searches: meta.searches };
    const report: Report = {
      id: crypto.randomUUID(),
      templateId,
      date: todayNY(now),
      generatedAt: Date.now(),
      provider,
      model: cfg.model,
      language: locale,
      markdown,
      inputs: { totalUsd: Math.round(totals.totalUsd), positions: assets.length, fxDate: fx?.date ?? null, scenario: opts.scenario?.trim() || undefined, context },
      usage,
      queries: meta.queries,
      sources: meta.sources,
      costUsd: actualCost(provider, cfg.model, usage),
      searchSuggestionsHtml: meta.searchSuggestionsHtml,
    };
    if (template.daily) {
      const previous = todaysReport(templateId, report.date);
      if (previous) await appStore.deleteReport(previous.id);
    }
    await appStore.putReport(report);
    appStore.setReportError(templateId, null);
    return report;
  } catch (e) {
    const message = t('report.failed', { name: templateName(templateId), error: describeError(e) });
    appStore.setReportError(templateId, message);
    appStore.notify(message, 'error');
    throw e;
  } finally {
    appStore.setReportBusy(null);
  }
}
